import type { QualityErrorCode } from './types';

/**
 * What a surface shows for a failed quality read (copy as English keys):
 * - `not-set-up`: "Website stats are not available yet", no button.
 * - `failed`: "We could not load your website stats" with `body` and "Try again".
 * - `refused`: `text` alone, no button (the selection is too large to count).
 * - `hosts-rejected`: the caller drops the addresses the answer names and asks again.
 */
export type QualityScreenState =
  | { kind: 'not-set-up' }
  | { kind: 'failed'; body: string }
  | { kind: 'refused'; text: string }
  | { kind: 'hosts-rejected' };

const GENERIC: QualityScreenState = { kind: 'failed', body: 'Something went wrong on our side. Please try again.' };

const STATES: Record<QualityErrorCode, QualityScreenState> = {
  analytics_unconfigured: { kind: 'not-set-up' },
  traffic_quality_unavailable: { kind: 'not-set-up' },
  analytics_busy: { kind: 'failed', body: 'Website traffic quality is busy; try again' },
  dataset_not_ready: { kind: 'failed', body: 'Website traffic data is not ready' },
  query_timeout: { kind: 'failed', body: 'Website traffic quality took too long' },
  hosts_unavailable: GENERIC,
  filter_limit_exceeded: { kind: 'refused', text: 'Too many filter conditions. Remove a choice to continue.' },
  query_limit_exceeded: { kind: 'refused', text: 'The website traffic request is too large to calculate' },
  host_not_owned: { kind: 'hosts-rejected' },
  invalid_request: GENERIC,
  request_too_large: GENERIC,
  analytics_unreachable: GENERIC,
  analytics_credential_refused: GENERIC,
  analytics_invalid_response: GENERIC,
  upstream_query_failed: GENERIC,
};

export const isQualityErrorCode = (code: string): code is QualityErrorCode => Object.hasOwn(STATES, code);

/** The screen state for a relay code; null (no code, or no answer) is the generic failure. */
export const qualityScreenState = (code: QualityErrorCode | null): QualityScreenState =>
  code === null ? GENERIC : STATES[code];
